import * as PIXI from 'pixi.js';

import type Luminary from './';

export class CanvasReusableTexturesManager {
    #luminary: Luminary;
    #detachedTextures: PIXI.Texture[] = [];
    #attachedTextures = new Map<PIXI.Texture, () => void>();
    #canvasResolutionMultiplier = 2;

    get canvasResolutionMultiplier() {
        return this.#canvasResolutionMultiplier;
    }

    set canvasResolutionMultiplier(value: number) {
        this.#canvasResolutionMultiplier = value;
    }

    constructor(luminary: Luminary) {
        this.#luminary = luminary;
    }

    getCanvasResolution() {
        const referenceToOutputMultiplier =
            this.#luminary.outputResolution.width / this.#luminary.referenceResolution.width;
        return {
            width:
                this.#luminary.referenceResolution.width *
                referenceToOutputMultiplier *
                this.canvasResolutionMultiplier,
            height:
                this.#luminary.referenceResolution.height *
                referenceToOutputMultiplier *
                this.canvasResolutionMultiplier,
        };
    }

    allocateTexture(detach: () => void): PIXI.Texture {
        const texture =
            this.#detachedTextures.pop() ||
            (() => {
                const canvas = document.createElement('canvas');
                const canvasResolution = this.getCanvasResolution();
                canvas.width = canvasResolution.width;
                canvas.height = canvasResolution.height;
                canvas.style.width = this.#luminary.outputResolution.width + 'px';
                canvas.style.height = this.#luminary.outputResolution.height + 'px';
                const texture = PIXI.Texture.from(canvas);
                texture.baseTexture.setSize(
                    this.#luminary.referenceResolution.width,
                    this.#luminary.referenceResolution.height,
                );
                return texture;
            })();
        this.#attachedTextures.set(texture, detach);
        return texture;
    }

    detachTextures() {
        for (const [texture, detach] of this.#attachedTextures) {
            detach();

            const textureWidth = this.#luminary.referenceResolution.width;
            const textureHeight = this.#luminary.referenceResolution.height;
            const rectangle = new PIXI.Rectangle(0, 0, textureWidth, textureHeight);
            texture.orig = rectangle;
            texture.frame = rectangle;
            texture.trim = rectangle;
            this.#detachedTextures.unshift(texture);
        }
        this.#attachedTextures.clear();
    }

    destroy() {
        this.detachTextures();
        for (const texture of this.#detachedTextures) {
            texture.destroy(true);
        }

        this.#detachedTextures = [];
    }

    getDebugInfo() {
        return {
            detachedTextures: this.#detachedTextures,
            attachedTextures: this.#attachedTextures,
        };
    }
}
